import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { brand } from '@iheart/shared';
import { useNotifications, useMarkRead, type Notification } from '../../lib/notifications';
import { useWorkspaceStore, type Workspace } from '../../lib/workspace';
import { useConfirm } from '../ConfirmDialog';
import { useChatWidgetStore } from './ChatWidgetStore';

/**
 * Pink message-preview cards stacked directly above the "Let's Chat" bubble.
 * Shows the most recent UNREAD chat conversations (cross-workspace) so new /
 * unread messages are visible without opening the panel. Tapping a card opens
 * that conversation (switching workspace first if needed); the ✕ dismisses it.
 * Rendered only while the chat panel is closed.
 */
export function ChatNotices() {
  const { data } = useNotifications({ unread: true });
  const markRead = useMarkRead();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const currentWs = useWorkspaceStore((s) => s.current);
  const setCurrentWs = useWorkspaceStore((s) => s.setCurrent);
  const { open, goToThread } = useChatWidgetStore();

  const notices = (data?.data ?? []).filter((n) => n.kind === 'chat.message').slice(0, 3);
  if (notices.length === 0) return null;

  async function openNotice(n: Notification) {
    const roomPublicId =
      n.payload && typeof (n.payload as { roomPublicId?: unknown }).roomPublicId === 'string'
        ? ((n.payload as { roomPublicId: string }).roomPublicId)
        : null;
    if (n.workspacePublicId && n.workspacePublicId !== currentWs?.publicId) {
      const targetName = n.workspaceName ?? 'another workspace';
      const ok = await confirm({
        title: 'Change workspace?',
        message: `This chat is in "${targetName}". You're currently in "${
          currentWs?.name ?? 'your current workspace'
        }". Continue and switch into "${targetName}"?`,
        confirmLabel: 'Yes, continue',
        cancelLabel: 'No, keep me here',
        variant: 'switch',
      });
      if (!ok) return;
      const full = qc.getQueryData<Workspace[]>(['workspaces'])?.find((w) => w.publicId === n.workspacePublicId);
      if (full) setCurrentWs(full);
    }
    markRead.mutate(n.id);
    open();
    if (roomPublicId) goToThread(roomPublicId);
  }

  return (
    <div className="fixed bottom-44 right-3 z-40 flex w-[min(300px,calc(100vw-1.5rem))] flex-col-reverse gap-2 md:bottom-48 md:right-5">
      <AnimatePresence>
        {notices.map((n) => (
          <motion.div
            key={n.id}
            layout
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.18 }}
            className="relative rounded-2xl px-3 py-2.5 text-white shadow-lg shadow-pink-500/20"
            style={{ backgroundImage: brand.gradient.primary }}
          >
            <button
              type="button"
              onClick={() => void openNotice(n)}
              className="block w-full pr-5 text-left"
            >
              <span className="block truncate text-xs font-semibold">{n.title}</span>
              {n.body && <span className="mt-0.5 block truncate text-[11px] text-white/90">{n.body}</span>}
            </button>
            <button
              type="button"
              onClick={() => markRead.mutate(n.id)}
              aria-label="Dismiss"
              title="Dismiss"
              className="absolute right-1.5 top-1.5 rounded-full p-0.5 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
            >
              <X className="size-3.5" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
